import {
  Check,
  Phone,
  MessageCircle,
  Calendar,
  MapPin,
  Plane,
  ShirtIcon,
  Sun,
  Heart,
  Video,
  Clock,
  CheckCircle2,
  Award,
  DollarSign,
  Activity,
  Mic,
  Sparkles,
  Leaf,
} from 'lucide-react';
import { useEffect, useState } from 'react';

export default function WelcomeDavidG() {
  const [daysUntilRetreat, setDaysUntilRetreat] = useState(0);
  const [daysUntilZoom, setDaysUntilZoom] = useState(0);

  useEffect(() => {
    const retreatDate = new Date('2026-07-23');
    const zoomDate = new Date('2026-06-24');
    const today = new Date();

    setDaysUntilRetreat(Math.ceil((retreatDate - today) / (1000 * 60 * 60 * 24)));
    setDaysUntilZoom(Math.ceil((zoomDate - today) / (1000 * 60 * 60 * 24)));
  }, []);

  const packingList = [
    {
      category: 'Essentials',
      icon: Plane,
      items: [
        'Valid Passport (not expiring within 6 months of travel)',
        'Travel insurance documents',
        'Any required medications',
        'Phone charger & adapter (Panama uses Type A/B plugs)',
        'Printed copy of your facilitator agenda (we will send ahead)',
      ],
    },
    {
      category: 'Movement & Wellness Gear',
      icon: Activity,
      items: [
        'Workout / yoga attire (2–3 sets)',
        'Breathwork or meditation mat (if you prefer your own)',
        'Resistance band or any small tools you use in sessions',
        'Swimwear for beach & pool',
        'Comfortable training shoes + sandals',
        'Refillable water bottle',
      ],
    },
    {
      category: 'Facilitator Attire',
      icon: ShirtIcon,
      items: [
        'Comfortable, breathable session-wear that allows demonstration',
        'One elevated outfit for the celebration dinner',
        'Casual clothes for off-time',
        'Light jacket for air-conditioned rooms',
      ],
    },
    {
      category: 'Your Toolkit',
      icon: Leaf,
      items: [
        'Notebook & pen for your sessions',
        'Any handouts or protocols you want printed (let us know in advance)',
        'Business cards (the brothers will want to stay connected)',
        'Sunscreen (SPF 30+), sunglasses & hat',
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#0f1d20] via-[#102a30] to-[#08161a]"
      data-testid="welcome-davidg-page"
    >
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <p
            className="text-teal-300 font-sans uppercase tracking-[0.3em] text-sm font-semibold mb-6"
            data-testid="welcome-davidg-eyebrow"
          >
            A Personal Welcome
          </p>

          <h1 className="font-serif text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Thank You, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-200">
              David.
            </span>
          </h1>

          <p className="text-slate-300 font-sans text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Your seat is confirmed and your role as a
            <span className="text-teal-300 font-semibold"> wellness facilitator</span> at The Reset
            is officially set. The brotherhood is going to feel the difference because you said yes.
          </p>

          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            <div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center"
              data-testid="countdown-retreat"
            >
              <p className="text-5xl font-bold text-teal-300 font-mono">{daysUntilRetreat}</p>
              <p className="text-slate-400 font-sans text-sm mt-2">Days Until Panama</p>
            </div>
            <div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center"
              data-testid="countdown-zoom"
            >
              <p className="text-5xl font-bold text-white font-mono">{daysUntilZoom}</p>
              <p className="text-slate-400 font-sans text-sm mt-2">Days Until Facilitator Zoom</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gratitude / Confirmation */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div
            className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-l-4 border-teal-400 rounded-r-2xl p-8 md:p-10"
            data-testid="payment-confirmation"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-teal-300" />
              </div>
              <p className="text-teal-300 font-sans uppercase tracking-wider text-sm font-semibold">
                Confirmation Received
              </p>
            </div>
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-3">
              David, thank you for completing your registration for{' '}
              <span className="text-white font-semibold">The Reset 2026</span>. A confirmation has
              been sent to your email.
            </p>
            <p className="text-slate-300 font-sans text-lg leading-relaxed">
              Bringing your work — presence, strength, and self-respect — into this brotherhood is a
              gift. We're deeply grateful you're walking this journey with us.
            </p>
          </div>
        </div>
      </section>

      {/* Facilitator Confirmation */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-teal-500/20 flex items-center justify-center mx-auto mb-4">
              <Award className="w-8 h-8 text-teal-300" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              You're Officially a Facilitator
            </h2>
            <p className="text-slate-400 font-sans">
              Honored to have your wellness work shaping this brotherhood
            </p>
          </div>

          <div className="bg-[#0f1d20] border border-teal-500/30 rounded-2xl p-8 shadow-xl shadow-teal-500/5">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Role</p>
                    <p className="text-slate-400 font-sans text-sm">
                      Wellness Coach & Facilitator — The HEALth Debt Coach™
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Your Sessions</p>
                    <p className="text-slate-400 font-sans text-sm">
                      Daily Morning Wellness Practice
                    </p>
                    <p className="text-slate-400 font-sans text-sm">
                      Workshop: Reconnect, Regulate & Reclaim Your Energy
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Brand Represented</p>
                    <p className="text-slate-400 font-sans text-sm">LiveNow Fitness</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <p className="text-teal-300 font-sans text-sm uppercase tracking-wider font-semibold mb-4">
                  What You'll Bring:
                </p>
                <ul className="space-y-3">
                  {[
                    'Sustainable self-care practices rooted in presence',
                    'Nervous-system regulation & breathwork',
                    'Movement that reconnects men to their bodies',
                    'The truth that self-care is infrastructure — not indulgence',
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-slate-300 font-sans text-sm"
                    >
                      <CheckCircle2 className="w-5 h-5 text-teal-300 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Honorarium */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div
            className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-2xl p-8 md:p-10"
            data-testid="honorarium-section"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-7 h-7 text-teal-300" />
              </div>
              <div>
                <p className="text-teal-300 font-sans uppercase tracking-wider text-xs font-bold mb-1">
                  Facilitator Honorarium
                </p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-white">
                  A Token of Our Gratitude
                </h2>
              </div>
            </div>

            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-4">
              In recognition of your contribution as a facilitator, an{' '}
              <span className="text-teal-300 font-semibold">honorarium will be paid to you within
              14 business days following the close of the retreat</span>.
            </p>
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-6">
              We'll confirm payment details (preferred method, mailing address, or wire
              instructions) during our facilitator Zoom call so everything is set well before you
              travel.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <p className="text-teal-300 font-sans text-xs uppercase tracking-wider mb-2">
                  When
                </p>
                <p className="text-white font-sans font-semibold">
                  Within 14 business days post-retreat
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <p className="text-teal-300 font-sans text-xs uppercase tracking-wider mb-2">
                  How
                </p>
                <p className="text-white font-sans font-semibold">
                  Your preferred method (Zelle, ACH, check)
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <p className="text-teal-300 font-sans text-xs uppercase tracking-wider mb-2">
                  Confirmation
                </p>
                <p className="text-white font-sans font-semibold">
                  We'll lock details on the Zoom call
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilitator Prep Call */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-teal-500/20 flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-teal-300" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              Facilitator Prep Call
            </h2>
            <p className="text-slate-400 font-sans">
              Let's align on sessions, logistics, and honorarium details
            </p>
          </div>

          <div className="bg-[#0f1d20] border border-teal-500/30 rounded-2xl p-8 shadow-xl shadow-teal-500/5">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Wednesday, June 24, 2026</p>
                    <p className="text-slate-500 font-sans text-sm">About a month before we meet</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">7:00 PM EST</p>
                    <p className="text-slate-500 font-sans text-sm">About 60 minutes</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                    <Video className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Via Zoom</p>
                    <p className="text-slate-500 font-sans text-sm">
                      Check your Google email for meeting details
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-6">
                <p className="text-teal-300 font-sans text-sm uppercase tracking-wider font-semibold mb-4">
                  What We'll Cover:
                </p>
                <ul className="space-y-3">
                  {[
                    'Align on wellness topics & flow for your sessions',
                    'Pre-work to send conference attendees ahead of the retreat',
                    'Confirm honorarium payment method & details',
                    'Travel logistics & airport pickup',
                    'Any A/V or material needs for your sessions',
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-slate-300 font-sans text-sm"
                    >
                      <CheckCircle2 className="w-5 h-5 text-teal-300 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Pack */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              What to Pack
            </h2>
            <p className="text-slate-400 font-sans">Your facilitator-ready guide for Panama</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {packingList.map((section, index) => {
              const Icon = section.icon;
              return (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-colors"
                  data-testid={`packing-${section.category.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                >
                  <h3 className="text-teal-300 font-sans font-semibold mb-4 flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    {section.category}
                  </h3>
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-teal-300 mt-1 flex-shrink-0" />
                        <span className="text-slate-300 font-sans text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Travel Details */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              Travel Details
            </h2>
            <p className="text-slate-400 font-sans">
              Everything you need to get to Panama with ease
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#0f1d20] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Plane className="w-6 h-6 text-teal-300" />
                <h3 className="text-white font-sans font-semibold">Arrival</h3>
              </div>
              <div className="space-y-2 text-slate-300 font-sans">
                <p>
                  <span className="text-slate-500">Date:</span> Thursday, July 23, 2026
                </p>
                <p>
                  <span className="text-slate-500">Airport:</span> Tocumen International (PTY)
                </p>
                <p>
                  <span className="text-slate-500">Arrive by:</span> 9:00 PM (earlier if possible —
                  facilitator briefing dinner)
                </p>
              </div>
              <p className="text-teal-300 font-sans text-sm mt-4 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Airport shuttle included — share your flight info with us
              </p>
            </div>
            <div className="bg-[#0f1d20] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-teal-300" />
                <h3 className="text-white font-sans font-semibold">Where We're Staying</h3>
              </div>
              <div className="space-y-2 text-slate-300 font-sans">
                <p>
                  <span className="text-slate-500">Hotel:</span> The Westin Playa Bonita
                </p>
                <p>
                  <span className="text-slate-500">Location:</span> Panama City, Panama
                </p>
                <p>
                  <span className="text-slate-500">Check-out:</span> Sunday, July 26 by 2:00 PM
                </p>
                <p>
                  <span className="text-slate-500">Distance:</span> ~30 min from PTY airport
                </p>
              </div>
              <a
                href="https://www.marriott.com/search/availabilityCalendar.mi?isRateCalendar=true&propertyCode=PTYBW"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-300 font-sans text-sm hover:underline mt-4 inline-block"
              >
                View Hotel Details →
              </a>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="bg-[#0f1d20] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-teal-300" />
                <h3 className="text-white font-sans font-semibold">Passport & Entry</h3>
              </div>
              <ul className="space-y-2 text-slate-300 font-sans text-sm">
                <li>U.S. citizens do not need a visa for stays under 180 days.</li>
                <li>Passport must be valid for at least 6 months past travel dates.</li>
                <li>Have proof of onward/return travel ready at immigration.</li>
              </ul>
            </div>
            <div className="bg-[#0f1d20] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Sun className="w-6 h-6 text-teal-300" />
                <h3 className="text-white font-sans font-semibold">On the Ground</h3>
              </div>
              <ul className="space-y-2 text-slate-300 font-sans text-sm">
                <li>Currency: USD is widely accepted alongside Panamanian Balboa.</li>
                <li>Weather: 80–88°F, humid with brief afternoon showers.</li>
                <li>Power: Type A/B plugs, 120V (same as U.S.).</li>
                <li>Language: Spanish (English widely spoken at hotel & airport).</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Got Questions, David?
          </h2>
          <p className="text-slate-400 font-sans mb-8">
            Anything at all — sessions, honorarium, travel — reach out anytime.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:804-310-7433"
              className="flex items-center gap-3 bg-teal-400 text-[#0f1d20] px-8 py-4 rounded-full font-sans font-semibold hover:bg-teal-300 transition-all hover:-translate-y-0.5 shadow-lg shadow-teal-500/25"
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
              Call: (804) 310-7433
            </a>
            <a
              href="sms:804-310-7433"
              className="flex items-center gap-3 bg-transparent border-2 border-teal-400 text-teal-300 px-8 py-4 rounded-full font-sans font-semibold hover:bg-teal-500/10 transition-all"
              data-testid="text-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>

          <p className="text-slate-500 font-sans text-sm mt-6">
            Or email:{' '}
            <a
              href="mailto:askcoachneil@gmail.com"
              className="text-teal-300 hover:underline"
            >
              askcoachneil@gmail.com
            </a>
          </p>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-20 px-6 bg-gradient-to-t from-teal-500/10 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-teal-300" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Thank You for Saying Yes
          </h2>
          <p className="text-slate-300 font-sans text-lg leading-relaxed mb-8">
            David, the way you teach men to slow down, listen inward, and reset how they show up
            for their lives is exactly what this brotherhood needs. We're honored to hold space
            with you in Panama.
          </p>
          <p className="text-teal-300 font-serif text-2xl italic">
            "Self-care is not indulgence — it's infrastructure."
          </p>
          <p className="text-slate-500 font-sans text-sm mt-6">
            — With deep gratitude, the Reset team
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600 font-sans text-sm">
            July 23–26, 2026 · Panama City, Panama · The Westin Playa Bonita
          </p>
        </div>
      </footer>
    </div>
  );
}
